import { Button, Typography } from "@pondigitalsolutions/pon-component-library";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { ImageLightboxItem } from "../types/ImageTypes";

interface ImageLightboxProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  modalImage: ImageLightboxItem | null;
  setModalImage: (image: ImageLightboxItem | null) => void;
  images: ImageLightboxItem[];
  currentImageIndex: number;
  setCurrentImageIndex: (index: number) => void;
}

const minScale = 1;
const maxScale = 2;
const commonButtonClasses =
  "h-12 w-12 rounded-full px-0 [&>svg]:h-5 [&>svg]:w-5";

const PREVIOUS = "previous";
const NEXT = "next";

export const ImageLightbox = ({
  isModalOpen,
  setIsModalOpen,
  modalImage,
  setModalImage,
  images,
  currentImageIndex,
  setCurrentImageIndex,
}: ImageLightboxProps) => {
  const { t } = useTranslation();

  const [scale, setScale] = useState(minScale);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });

  const modalRef = useRef<HTMLDivElement>(null);

  // Navigation functionality
  const navigate = useCallback(
    (direction: typeof PREVIOUS | typeof NEXT) => {
      let newIndex: number;

      if (direction === PREVIOUS) {
        newIndex =
          currentImageIndex > 0 ? currentImageIndex - 1 : images.length - 1;
      } else {
        newIndex =
          currentImageIndex < images.length - 1 ? currentImageIndex + 1 : 0;
      }

      setCurrentImageIndex(newIndex);
      setModalImage(images[newIndex]);
      // Reset zoom and translation when navigating
      setScale(minScale);
      setTranslate({ x: 0, y: 0 });
    },
    [
      currentImageIndex,
      images,
      setCurrentImageIndex,
      setModalImage,
      setScale,
      setTranslate,
    ]
  );

  // Zooming functionality
  const round = (num: number) => Math.round(num * 10) / 10;
  const zoomIn = () =>
    setScale((prev) => round(Math.min(prev + 0.2, maxScale)));
  const zoomOut = () => {
    setScale((prev) => {
      const newScale = round(Math.max(prev - 0.2, minScale));
      if (newScale === minScale) {
        setTranslate({ x: 0, y: 0 });
      }
      return newScale;
    });
  };
  const onWheel = (e: React.WheelEvent) => {
    if (e.deltaY < 0) {
      zoomIn();
    } else {
      const prev = scale;
      zoomOut();
      if (prev !== minScale && scale === minScale) {
        setTranslate({ x: 0, y: 0 });
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(null);
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  const maxTranslationValues = (containerRect: DOMRect) => {
    // Get the actual image dimensions after scaling
    const scaledImageWidth = containerRect.width * scale;
    const scaledImageHeight = containerRect.height * scale;

    // Calculate maximum translation values to keep image within bounds
    const maxTranslateX = Math.max(
      0,
      (scaledImageWidth - containerRect.width) / 2
    );
    const maxTranslateY = Math.max(
      0,
      (scaledImageHeight - containerRect.height) / 2
    );

    return { maxTranslateX, maxTranslateY };
  };

  // Dragging functionality
  const startDragging = (e: React.MouseEvent) => {
    if (scale === minScale) return;
    setIsDragging(true);
    setStart({ x: e.clientX - translate.x, y: e.clientY - translate.y });
  };
  const onDrag = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const newTranslateX = e.clientX - start.x;
    const newTranslateY = e.clientY - start.y;

    // Calculate the bounds based on the container and scaled image dimensions
    const container = e.currentTarget as HTMLElement;
    const containerRect = container.getBoundingClientRect();
    const { maxTranslateX, maxTranslateY } =
      maxTranslationValues(containerRect);

    // Constrain the translation within bounds
    const constrainedX = Math.max(
      -maxTranslateX,
      Math.min(maxTranslateX, newTranslateX)
    );
    const constrainedY = Math.max(
      -maxTranslateY,
      Math.min(maxTranslateY, newTranslateY)
    );

    setTranslate({ x: constrainedX, y: constrainedY });
  };
  const stopDragging = () => setIsDragging(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isModalOpen) return;

      // Handle Escape key to close the modal
      if (event.key === "Escape") {
        setIsModalOpen(false);
        setModalImage(null);
        return;
      }

      // Handle arrow keys for navigation (only when not zoomed in)
      if (scale === minScale) {
        const navigationMap: Record<string, typeof PREVIOUS | typeof NEXT> = {
          ArrowLeft: PREVIOUS,
          ArrowRight: NEXT,
        };
        const direction = navigationMap[event.key];
        if (direction) {
          event.preventDefault();
          navigate(direction);
          return;
        }
      }

      // Handle Tab key for focus trapping
      if (event.key === "Tab" && modalRef.current) {
        // Select all focusable elements within the modal.
        const focusableElements = modalRef.current.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"]):not([disabled])'
        );
        const focusableArray = Array.from(focusableElements) as HTMLElement[];
        const firstElement = focusableArray[0];
        const lastElement = focusableArray[focusableArray.length - 1];

        // If no focusable elements, prevent tabbing
        if (focusableArray.length === 0) {
          event.preventDefault();
          return;
        }

        if (event.shiftKey) {
          // Shift + Tab: moving backwards
          if (
            document.activeElement === firstElement ||
            !modalRef.current.contains(document.activeElement)
          ) {
            event.preventDefault();
            lastElement?.focus();
          }
          // Tab: moving forwards
        } else if (
          document.activeElement === lastElement ||
          !modalRef.current.contains(document.activeElement)
        ) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleKeyDown);

      // Disable body scrolling when modal is open
      // This prevents the background from scrolling when the modal is open
      document.body.style.overflow = "hidden";

      // Disable scrolling on mobile.
      document.ontouchmove = function (e) {
        e.preventDefault();
      };
    }

    return () => {
      // Cleanup event listener
      document.removeEventListener("keydown", handleKeyDown);

      // Re-enable body scrolling
      document.body.style.overflow = "unset";
      document.ontouchmove = function () {
        return true;
      };
    };
  }, [isModalOpen, setIsModalOpen, setModalImage, scale, navigate]);

  if (!isModalOpen && !modalImage) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-20 z-100 flex items-center justify-center bg-secondary-900/50"
    >
      <Button
        variant="primary"
        id="close-modal"
        aria-label={t("ImageGallery.closeImage")}
        onClick={closeModal}
        startIcon="XClose"
        className={twMerge(
          "absolute top-4 right-4 z-10 hover:cursor-pointer",
          commonButtonClasses
        )}
      />

      {/* Navigation Controls */}
      {images.length > 1 && (
        <>
          <Button
            variant="primary"
            id="previous-image-button"
            aria-label={t("ImageGallery.previousImage")}
            onClick={() => navigate(PREVIOUS)}
            startIcon="ArrowNarrowLeft"
            className={twMerge(
              "absolute top-1/2 left-4 z-30 hover:cursor-pointer",
              commonButtonClasses
            )}
          />
          <Button
            variant="primary"
            id="next-image-button"
            aria-label={t("ImageGallery.nextImage")}
            onClick={() => navigate(NEXT)}
            startIcon="ArrowNarrowRight"
            className={twMerge(
              "absolute top-1/2 right-4 z-30 hover:cursor-pointer",
              commonButtonClasses
            )}
          />
        </>
      )}

      {/* Zoom Controls */}
      <div className="absolute bottom-4 z-30 hidden flex-row items-center gap-2 lg:flex">
        <Button
          variant="primary"
          id="zoom-in-button"
          aria-label={t("ImageGallery.zoomIn")}
          onClick={zoomIn}
          startIcon="ZoomIn"
          className={twMerge(
            commonButtonClasses,
            scale >= maxScale ? "" : "hover:cursor-pointer"
          )}
          disabled={scale >= maxScale}
        />
        <Button
          variant="primary"
          id="zoom-out-button"
          aria-label={t("ImageGallery.zoomOut")}
          onClick={zoomOut}
          startIcon="ZoomOut"
          className={twMerge(
            commonButtonClasses,
            scale <= minScale ? "" : "hover:cursor-pointer"
          )}
          disabled={scale <= minScale}
        />
      </div>

      <button
        aria-label={t("ImageGallery.modalImage")}
        className="flex h-full w-full cursor-grab items-center justify-center overflow-hidden active:cursor-grabbing"
        onMouseDown={startDragging}
        onMouseMove={onDrag}
        onMouseUp={stopDragging}
        onMouseLeave={stopDragging}
        onWheel={onWheel}
        onClick={(e) => {
          // Only close when the backdrop (not its children) is clicked
          if (e.currentTarget === e.target) {
            closeModal();
          }
        }}
        onKeyDown={(e) => {
          // Handle keyboard interactions for accessibility
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
          }
          // Arrow keys for panning when zoomed in
          if (scale > minScale) {
            const panAmount = 50;
            const container = e.currentTarget as HTMLElement;
            const containerRect = container.getBoundingClientRect();
            const { maxTranslateX, maxTranslateY } =
              maxTranslationValues(containerRect);

            switch (e.key) {
              case "ArrowLeft":
                e.preventDefault();
                setTranslate((prev) => ({
                  ...prev,
                  x: Math.min(maxTranslateX, prev.x + panAmount),
                }));
                break;
              case "ArrowRight":
                e.preventDefault();
                setTranslate((prev) => ({
                  ...prev,
                  x: Math.max(-maxTranslateX, prev.x - panAmount),
                }));
                break;
              case "ArrowUp":
                e.preventDefault();
                setTranslate((prev) => ({
                  ...prev,
                  y: Math.min(maxTranslateY, prev.y + panAmount),
                }));
                break;
              case "ArrowDown":
                e.preventDefault();
                setTranslate((prev) => ({
                  ...prev,
                  y: Math.max(-maxTranslateY, prev.y - panAmount),
                }));
                break;
            }
          }
        }}
      >
        <img
          src={modalImage?.image}
          style={{
            transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
            transition: isDragging ? "none" : "transform 0.2s",
            transformOrigin: "center center",
          }}
          className="mx-auto select-none"
          draggable={false}
          alt={modalImage?.alt ?? ""}
        />
        {modalImage?.caption && (
          <Typography className="absolute bottom-5 left-4 rounded-full bg-white/20 px-3.5 py-3 leading-[100%] text-white">
            {modalImage?.caption}
          </Typography>
        )}
      </button>
    </div>
  );
};
